import { useEffect, useState } from 'react';
import {
  addRewardItem,
  fetchRewardItems,
  removeRewardItem,
  saveRewardItem,
  type RewardItem,
} from '../rewards/rewards';
import { useSettings } from './SettingsContext';
import { useT } from '../i18n/i18n';

export default function RewardItemsSection() {
  const t = useT();
  const { settings } = useSettings();
  const [items, setItems] = useState<RewardItem[]>([]);
  const [name, setName] = useState('');
  const [cost, setCost] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const plural = settings.reward.name.trim() || t('rewards');

  useEffect(() => {
    let cancelled = false;
    void fetchRewardItems()
      .then((list) => { if (!cancelled) setItems(list); })
      .catch(() => { if (!cancelled) setItems([]); });
    return () => { cancelled = true; };
  }, []);

  async function add() {
    if (!name.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const created = await addRewardItem(name.trim(), cost);
      setItems((cur) => [...cur, created]);
      setName('');
      setCost(0);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('That could not be added.'));
    } finally {
      setBusy(false);
    }
  }

  async function edit(item: RewardItem, nextName: string, nextCost: number) {
    setError(null);
    try {
      const saved = await saveRewardItem(item.id, nextName, nextCost);
      setItems((cur) => cur.map((i) => (i.id === saved.id ? saved : i)));
    } catch (e) {
      setError(e instanceof Error ? e.message : t('That could not be saved.'));
    }
  }

  async function remove(item: RewardItem) {
    setError(null);
    try {
      await removeRewardItem(item.id);
      setItems((cur) => cur.filter((i) => i.id !== item.id));
    } catch (e) {
      setError(e instanceof Error ? e.message : t('That could not be removed.'));
    }
  }

  return (
    <div className="card">
      <h2>{t('What they can choose')}</h2>
      <p className="field-hint" style={{ marginTop: 0 }}>
        {t('Things a learner can pick by tapping their total, and what each costs in {name}. Something can cost nothing at all. Choosing does not touch what they have earned — that record stays as it is.', { name: plural })}
      </p>

      {error && <div className="error" role="alert">{error}</div>}

      {items.length === 0 ? (
        <p className="muted">{t('Nothing on the list yet.')}</p>
      ) : (
        <ul className="list">
          {items.map((item) => (
            <li className="list-item" key={item.id}>
              <div className="row" style={{ flex: 1 }}>
                <div className="field">
                  <label htmlFor={`item-name-${item.id}`}>{t('Name')}</label>
                  <input
                    id={`item-name-${item.id}`}
                    defaultValue={item.name}
                    maxLength={80}
                    onBlur={(e) => {
                      const v = e.target.value.trim();
                      if (v && v !== item.name) void edit(item, v, item.cost);
                    }}
                  />
                </div>
                <div className="field">
                  <label htmlFor={`item-cost-${item.id}`}>{t('Costs')}</label>
                  <input
                    id={`item-cost-${item.id}`}
                    type="number"
                    min={0}
                    max={9999}
                    defaultValue={item.cost}
                    onBlur={(e) => {
                      const n = Number(e.target.value);
                      if (!Number.isNaN(n) && n >= 0 && n !== item.cost) {
                        void edit(item, item.name, Math.min(9999, Math.round(n)));
                      }
                    }}
                  />
                </div>
              </div>
              <div className="toolbar">
                <button
                  type="button"
                  className="btn secondary danger"
                  onClick={() => { void remove(item); }}
                >
                  {t('Remove')}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <div className="row">
        <div className="field">
          <label htmlFor="new-item-name">{t('Add something')}</label>
          <input
            id="new-item-name"
            value={name}
            maxLength={80}
            placeholder={t('Ten minutes on the swing')}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="new-item-cost">{t('Costs')}</label>
          <input
            id="new-item-cost"
            type="number"
            min={0}
            max={9999}
            value={cost}
            onChange={(e) => {
              const n = Number(e.target.value);
              if (!Number.isNaN(n)) setCost(Math.max(0, Math.min(9999, Math.round(n))));
            }}
          />
          <span className="field-hint">{t('Zero is allowed.')}</span>
        </div>
      </div>

      <div className="form-actions">
        <button className="btn" onClick={() => { void add(); }} disabled={busy || !name.trim()}>
          {busy ? t('Adding…') : t('Add to the list')}
        </button>
      </div>
    </div>
  );
}
